import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { CustomerRatingReport } from '../types';

/** Ispod ovoliko ocena prosek je siv — isto kao `OcenaKupca.MalUzorak` na serveru. */
const MAL_UZORAK = 10;

function zvezdice(prosek: number): string {
  const pune = Math.round(prosek);
  return '★'.repeat(pune) + '☆'.repeat(5 - pune);
}

/**
 * Kako kupci ocenjuju prevoz i isporuku, po vozaču.
 *
 * Zahtev vlasnika, 16.10.2026: vozač je onaj koji ostavlja utisak — treba da zna
 * da ga neko ocenjuje, a operater i uprava da to vide i prate.
 *
 * Uz prosek stoji i koliko je isporuka OCENJENO od ponuđenih: kupac ne mora da
 * oceni, pa vozač sa svakom isporukom ocenjenom peticom traži pogled — možda je
 * kliktao sam. Ponovljene ocene i ocene bez potpisa se ne broje, ali se vide.
 */
export default function CustomerRatings({ days }: { days: number }) {
  const [report, setReport] = useState<CustomerRatingReport | null>(null);
  const [greska, setGreska] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setReport(await api.customerRatings(days));
      setGreska(null);
    } catch (e) {
      setReport(null);
      setGreska(e instanceof Error ? e.message : String(e));
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  return (
    <section className="panel">
      <h2>Ocene kupaca</h2>

      <p className="note">
        Kupac ocenjuje prevoz i isporuku zvezdicama u kućici za potpis, kad se
        potpiše. Ocena nije obavezna. Komentar može samo uz jednu zvezdicu.
      </p>

      {greska && <div className="health health--bad">Nije se moglo učitati: {greska}</div>}
      {!greska && !report && <p className="muted">Učitavam…</p>}

      {report && report.drivers.length === 0 && (
        <p className="muted">U poslednjih {days} dana nijedan kupac nije ocenio isporuku.</p>
      )}

      {report && report.drivers.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <th>Vozač</th>
              <th>Ocena</th>
              <th>Ocenjeno</th>
              <th>1★ · 2★ · 3★ · 4★ · 5★</th>
            </tr>
          </thead>
          <tbody>
            {report.drivers.map((v) => (
              <tr key={`${v.driverId ?? 0}|${v.driver}`}>
                <td>
                  {v.driver}
                  {v.checkWhy && (
                    <span className="flag flag--warn" title={v.checkWhy}> proveri: {v.checkWhy}</span>
                  )}
                </td>
                <td className={v.rated < MAL_UZORAK ? 'muted' : undefined}>
                  {v.average === null ? '—' : (
                    <>
                      <span className="ocene__zvezdice">{zvezdice(v.average)}</span>{' '}
                      {v.average.toFixed(2)}
                    </>
                  )}
                </td>
                <td>
                  {v.rated} od {v.signed}
                  {v.signed > 0 && (
                    <span className="muted"> ({Math.round((v.rated / v.signed) * 100)}%)</span>
                  )}
                </td>
                <td>
                  {v.byStars.map((broj, i) => (
                    <span key={i} className={i === 0 && broj > 0 ? 'flag flag--bad' : undefined}>
                      {i > 0 ? ' · ' : ''}{broj}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {report && report.comments.length > 0 && (
        <>
          <h3>Komentari uz jednu zvezdicu</h3>
          <ul className="rows">
            {report.comments.map((k) => (
              <li key={`${k.tourId}|${k.at}|${k.customer}`}>
                <span className="row__name">„{k.comment}"</span>
                <span className="row__side muted">
                  {new Date(k.at).toLocaleDateString('sr-RS')}
                </span>
                <span className="row__meta">
                  {k.driver} · {k.customer}{k.city ? `, ${k.city}` : ''} · tura {k.tourId}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
